import React, { useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { stringify } from 'query-string';

import { isAssigned } from 'actions/ab';
import { setFederatedLoginModalVisibility } from 'actions/headerfooter';
import { HYDRA_FEDERATED_LOGIN } from 'constants/hydraTests';
import useMartyContext from 'hooks/useMartyContext';
import { cartIsLoading, changeQuantity, showCartModal } from 'actions/cart';
import { heartProduct } from 'actions/hearts';
import { toUSD } from 'helpers/NumberFormats';
import CartQuantityDropdown from 'components/cart/CartQuantityDropdown';
import ProductImage from 'components/cart/ProductImage';
import { SmallLoader } from 'components/Loader';
import ItemDescription from 'components/cart/ItemDescription';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import FinalSale from 'zen/components/common/FinalSale';
import { CART_PAGE } from 'constants/amethystPageTypes';
import { trackEvent } from 'helpers/analytics';

import css from 'styles/components/cart/cartItem.scss';

export const CartItem = ({
  addedStockId,
  cartIsLoading,
  changeQuantity,
  children,
  className,
  forceRemoveButton,
  hasFullDetails = true,
  heartProduct,
  hideFavorite = false,
  hydraDoubleProductRemove,
  hydraFederatedLogin,
  isCustomer,
  isFavorite = false,
  isModal = false,
  isNotRecommendedFit = false,
  isRecommendedFit = false,
  isUnavailable,
  item,
  routing,
  setFederatedLoginModalVisibility,
  showCartModal,
  showFixedQuantity
}) => {
  const [updating, setUpdating] = useState(null);

  const {
    testId,
    marketplace: { authUrl, checkout: { allowMoveToFavorites } }
  } = useMartyContext();

  const onItemUpdate = asin => {
    cartIsLoading();
    setUpdating(asin);
  };

  const onItemUpdateDone = () => {
    setUpdating(null);
  };

  const onMoveToFavorites = ({ currentTarget }) => {
    const { asin } = currentTarget.dataset;

    if (isCustomer) {
      onItemUpdate(asin);
      const itemData = { asin, quantity: 0 };

      const callback = () => {
        changeQuantity({ 'items': [itemData] });
        trackEvent('TE_CART_ADDTOFAVORITES', asin);
      };

      heartProduct({ subItemId: asin, sourcePage: CART_PAGE }, callback);
    } else {
      if (hydraFederatedLogin) {
        const { locationBeforeTransitions: { pathname, query } } = routing;
        const returnTo = encodeURIComponent(`${pathname}?${stringify({ ...query })}`);
        showCartModal(false);
        setFederatedLoginModalVisibility(true, { returnTo });
      } else {
        window.location.href = `${authUrl}/cart`;
      }
    }
  };

  const makeCartActions = () => {
    if (!hasFullDetails) {
      return null;
    }

    const { asin, egc } = item;

    const forceRemove = hydraDoubleProductRemove && forceRemoveButton;

    return (
      <div className={css.actions}>
        { !!showFixedQuantity && <><span className={css.fixedQuantity}>Quantity:</span> {showFixedQuantity}</> }

        {
          !showFixedQuantity && <CartQuantityDropdown
            isUnavailable={isUnavailable}
            item={item}
            forceRemoveButton={forceRemove}
            hydraDoubleProductRemove={hydraDoubleProductRemove}
            onChangeQuantityCb={onItemUpdate}
            onChangeQuantityCbDone={onItemUpdateDone} />
        }

        { (!hideFavorite && allowMoveToFavorites && !(egc || isUnavailable))
          && <button
            type="button"
            className={css.moveToFavs}
            onClick={onMoveToFavorites}
            data-test-id={testId('moveToFavoritesButton')}
            data-asin={asin}>Move to <span>Favorites</span></button> }
      </div>
    );
  };

  const makePrice = ({ price, originalPrice, finalSale }, stock, isUnavailable) => {
    if (isUnavailable) {
      return (
        <em className={css.unavailable} data-test-id={testId('unavailable')}>Unavailable</em>
      );
    }

    const isDiscounted = originalPrice > price;

    return (
      <>
        <em className={cn(css.price, { [css.discount]: isDiscounted })} data-test-id={testId('price')}>{toUSD(stock * price)}</em>
        { isDiscounted && <span data-test-id={testId('msrpPrice')}>MSRP: {toUSD(stock * originalPrice)}</span>}
        { stock !== 1 && <span className={css.each}>{toUSD(price)} each</span> }
        { finalSale && <FinalSale /> }
      </>
    );
  };

  const { egc, asin, stockId, quantity = 1 } = item;

  const stock = isFavorite ? 1 : quantity;

  const testIdName = egc ? 'giftCardItem' : isFavorite ? 'favItem' : 'cartItem';

  const showNotRecommendedBadge = isNotRecommendedFit && hydraDoubleProductRemove;

  return (
    <div
      data-test-id={testId(testIdName)}
      className={cn(className, css.container, {
        [css.favorite]: isFavorite,
        [css.compress]: isModal,
        [css.fade]: addedStockId && addedStockId === stockId
      })}>
      { asin === updating && <div className={css.updating}><SmallLoader /></div> }
      <ProductImage item={item} className={css.image} />
      <div className={css.midColumn}>
        <ItemDescription
          isUnavailable={isUnavailable}
          isModal={isModal}
          item={item}
          isRecommendedFit={isRecommendedFit}
          isNotRecommendedFit={showNotRecommendedBadge}
        />

        { makeCartActions() }

        <div className={css.priceContainer}>
          { children }
          { makePrice(item, stock, isUnavailable) }
        </div>
      </div>

    </div>
  );
};

CartItem.propTypes = {
  item: PropTypes.object.isRequired
};

export function mapStateToProps(state) {
  const { cart: { addedStockId }, cookies, routing } = state;
  const isCustomer = !!cookies['x-main'];

  return {
    addedStockId,
    isCustomer,
    hydraFederatedLogin: isAssigned(HYDRA_FEDERATED_LOGIN, 1, state),
    routing
  };
}

const ConnectedCartItem = connect(mapStateToProps, {
  cartIsLoading,
  changeQuantity,
  heartProduct,
  setFederatedLoginModalVisibility,
  showCartModal
})(CartItem);

export default withErrorBoundary('CartItem', ConnectedCartItem);
